import { useMemo } from 'react';
import type { LifetimeInputFieldConfig } from '@igus/tribology-domain/src/lib/lifetime/lifetime-calculator-definitions';
import { UnitSystem } from '@igus/kopla-domain';

const IMPERIAL_VALUE_PRECISION = 5;
const IMPERIAL_VALUE_PRECISION_MULTIPLIER = 10 ** IMPERIAL_VALUE_PRECISION;

const roundToImperialPrecision = (value: number, roundingMethod = Math.round): number =>
  roundingMethod(value * IMPERIAL_VALUE_PRECISION_MULTIPLIER) / IMPERIAL_VALUE_PRECISION_MULTIPLIER;

export interface ValidationResult {
  min: number;
  max: number;
  isValid: boolean;
}

export interface UseInputValidationParams {
  formUnitSystem: UnitSystem;
}

export interface UseInputValidationReturn {
  validateInput: (value: string | number, attributeConfig: LifetimeInputFieldConfig) => ValidationResult;
  getProperValuesForUnitSystem: (
    value: number,
    attributeConfig: LifetimeInputFieldConfig,
    unitSystemValue: UnitSystem
  ) => number;
}

export const useInputValidation = ({ formUnitSystem }: UseInputValidationParams): UseInputValidationReturn => {
  const getProperValuesForUnitSystem = useMemo(
    () =>
      (value: number, attributeConfig: LifetimeInputFieldConfig, unitSystemValue: UnitSystem): number =>
        unitSystemValue === UnitSystem.imperial && attributeConfig.toImperial
          ? roundToImperialPrecision(attributeConfig.toImperial(value))
          : value,
    []
  );

  const validateInput = useMemo(
    () =>
      (value: string | number, attributeConfig: LifetimeInputFieldConfig): ValidationResult => {
        const usesImperialConversion = formUnitSystem === UnitSystem.imperial && attributeConfig.toImperial;
        const min = usesImperialConversion
          ? roundToImperialPrecision(attributeConfig.toImperial(attributeConfig.min), Math.ceil)
          : attributeConfig.min;
        const max = usesImperialConversion
          ? roundToImperialPrecision(attributeConfig.toImperial(attributeConfig.max), Math.floor)
          : attributeConfig.max;

        if (!value && value !== 0) {
          return { isValid: false, min, max };
        }

        const normalizedValue = usesImperialConversion ? roundToImperialPrecision(Number(value)) : Number(value);

        return { isValid: normalizedValue >= min && normalizedValue <= max, min, max };
      },
    [formUnitSystem]
  );

  return {
    validateInput,
    getProperValuesForUnitSystem,
  };
};
